import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// Auth + session-scoped storage keys.
// IMPORTANT: tokens live in sessionStorage (NOT localStorage). They are tab-
// scoped, cleared on tab close and not shared across windows/tabs. This is the
// best browser-side mitigation we can apply on this platform — httpOnly
// cookies are the ideal approach but the Emergent preview ingress forces
// `Access-Control-Allow-Origin: *`, which browsers reject in credentialed
// requests, so we cannot use `withCredentials` here.
export const AUTH_TOKEN_KEY = "havs_token";
export const SELECTED_WORKER_KEY = "havs_selected_worker";

// HSE point thresholds (UK HSE).
export const ACTION_POINTS = 100;
export const LIMIT_POINTS = 400;
export const WATCH_POINTS = 80;

export const getAuthToken = () => sessionStorage.getItem(AUTH_TOKEN_KEY) || "";
export const setAuthToken = (token) => sessionStorage.setItem(AUTH_TOKEN_KEY, token);
export const clearAuthToken = () => sessionStorage.removeItem(AUTH_TOKEN_KEY);

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global trial / subscription gate: when the backend rejects with 402
// (Payment Required), we route to /locked. Other consumers can still see the
// error and toast on it.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (
      err?.response?.status === 402 &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/locked"
    ) {
      window.location.replace("/locked");
    }
    return Promise.reject(err);
  }
);

export function statusFromPoints(points) {
  if (points >= LIMIT_POINTS) return "stop";
  if (points >= ACTION_POINTS) return "action";
  if (points >= WATCH_POINTS) return "watch";
  return "ok";
}

export function statusLabel(status) {
  return (
    {
      ok: "OK",
      watch: "WATCH",
      action: "ACTION",
      stop: "STOP / LIMIT",
    }[status] || "OK"
  );
}

export const statusClass = (status) => `status-${status}`;
export const statusBar = (status) => `bar-${status}`;

// HSE: points = 2 * vibration^2 * hours
export function computePoints(vibration, triggerSeconds) {
  const hours = triggerSeconds / 3600;
  return Math.round(2 * vibration * vibration * hours * 10) / 10;
}

export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

/** Trigger a browser download for an authenticated CSV endpoint. */
export async function downloadCsv(url, filename) {
  const token = getAuthToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
